import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plan } from "../../Entities/Plan";
import { Subscription } from "../../Entities/Subscription";
import AuthService from "../../Service/auth.service";
import organizationService from "../../Service/organization.service";
import SubscriptionService from "../../Service/subscription.service";
import jobDescriptionService from "../../Service/job.description.service";
import { Flex, Spin, Typography } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Meta from "antd/es/card/Meta";
import RenewPlan from "../../Components/RenewPlan/RenewPlan";
import OrgOnBoarding from "./components/OrgOnBoarding";
import "./PreLoad.scss";

const LOADING_MESSAGES = [
  "Verifying your account details...",
  "Loading your organization settings...",
  "Checking your subscription plan...",
  "Fetching your job posts...",
  "Preparing your workspace...",
];
const PreLoad: React.FC = () => {
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Please wait...");
  const [descriptionIndex, setDescriptionIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  // Cycle through description messages every 2 seconds while loading
  useEffect(() => {
    if (!loading) return;

    const interval = setInterval(() => {
      setDescriptionIndex((prev) => {
        const nextIndex = prev + 1;
        if (nextIndex < LOADING_MESSAGES.length) {
          setAnimationKey((key) => key + 1);
          return nextIndex;
        }
        return prev;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [loading]);
  
  useEffect(() => {
    const checkAndNavigate = async (userId?: string, isAdmin?: boolean) => {
      AuthService.getInstance().handleInitialSetupDone();

      // Non-admin users (team members, interviewers, etc.) skip onboarding entirely
      if (!isAdmin) {
        navigate(redirect || "/", { replace: true });
        return;
      }

      // Layer 1: fast localStorage check
      const flagKey = userId ? `onboarding_completed_${userId}` : null;
      if (flagKey && localStorage.getItem(flagKey) === "true") {
        navigate(redirect || "/", { replace: true });
        return;
      }

      // Layer 2: fallback — check if user already has job posts (handles cleared localStorage)
      try {
        const result = await jobDescriptionService.getAllJobDescriptionwithFilters(
          1, 1, { search: null, status: null, sort: null, order: null }
        );
        if ((result?.totalRecords ?? 0) > 0) {
          if (flagKey) localStorage.setItem(flagKey, "true");
          navigate(redirect || "/", { replace: true });
          return;
        }
      } catch {
        // If API fails, fall through to show onboarding (safe default for new users)
      }

      navigate("/user-onboarding", { replace: true });
    };

    const handleOrganizationOnBoarding = async (
      subscription: Subscription | null
    ) => {
      try {
        setLoadingMessage("Loading organization data...");
        const userData = AuthService.getInstance().getCurrentUser();
        if (!userData) {
          console.error(
            "User not found on preload -> handleOrganizationOnBoarding"
          );
          return;
        }
        const isOrgAdmin = userData?.role === "admin";

        if (isOrgAdmin) {
          const orgData = await organizationService.getOrganizationData();
          if (!orgData) {
            console.error(
              "Organization not found on preload -> handleOrganizationOnBoarding"
            );
            return;
          }
          if (orgData?.theme) {
            localStorage.setItem("customTheme", JSON.stringify(orgData.theme));
          }
          const isMissingCurrency = !orgData?.currency;
          const isMissingTimeZone = !orgData?.timeZone;

          const isShowOnboarding = isMissingCurrency || isMissingTimeZone;

          if (isShowOnboarding) {
            setShowOnboarding(isShowOnboarding);
          } else if (subscription?.status === "active") {
            await checkAndNavigate(userData?._id, true);
            return;
          }
        } else if (subscription?.status === "active") {
          await checkAndNavigate(userData?._id, false);
          return;
        }
      } catch (error: any) {
        console.error("Error in handleOrganizationOnBoarding: preload", error);
      }
    };

    const loadData = async () => {
      setLoading(true);
      try {
        AuthService.getInstance().init();
        await organizationService.init();
        setLoadingMessage("Loading subscription data...");
        const data =
          await SubscriptionService.getCurrentAccess();

        if (data) {
          setPlan(data.plan);
          setSubscription(data.subscription);
        }

        await handleOrganizationOnBoarding(data?.subscription);
      } catch (error) {
        console.error("Error loading data on preload:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [redirect, navigate]);

  if (loading) {
    return (
      <Flex style={{ height: "100vh" }} align="center" justify="center">
        <Flex vertical align="flex-start" justify="center">
          <Spin indicator={<LoadingOutlined spin />} />
          <Meta
            title={
              <Typography.Title type="secondary" level={3}>
                {loadingMessage}
              </Typography.Title>
            }
            description={
              <span key={animationKey} className="loading-description-message">
                {LOADING_MESSAGES[descriptionIndex]}
              </span>
            }
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex style={{ margin: 16 }} align="start" justify="center">
      {subscription && subscription.status === "inactive" && plan ? (
        <RenewPlan plan={plan} subscription={subscription} />
      ) : showOnboarding ? (
        <OrgOnBoarding plan={plan} />
      ) : null}
    </Flex>
  );
};

export default PreLoad;
